/**
 * AdminUtenteDetailPage — F-012 Phase 3 (IMPL-P3-011).
 *
 * Per-account detail for fido management: WalletSummaryCard + FidoEditor in the
 * primary column, AuditTrailViewer in the secondary column on `lg+`, stacked to
 * a single column below.
 *
 * Structure mirrors AdminClientiPage: guard + layout + feature-local
 * QueryClientProvider wrapping a testable `*Inner`. The inner reads `:id` via
 * `useParams`, drives `useCliente`, and renders loading / 404 / content states.
 */
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { adminQueryClient } from '../lib/query-client';
import { errorMessageFor, isAdminApiError } from '../lib/admin-client';
import { useCliente } from '../hooks/useCliente';
import { WalletSummaryCard } from '../components/WalletSummaryCard';
import { FidoEditor } from '../components/FidoEditor';
import { AuditTrailViewer } from '../components/AuditTrailViewer';

export function AdminUtenteDetailPage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={adminQueryClient}>
          <AdminUtenteDetailPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminUtenteDetailPageInner() {
  const { id } = useParams<{ id: string }>();
  const query = useCliente(id);

  const notFound =
    query.isError && isAdminApiError(query.error) && query.error.status === 404;

  return (
    <section className="space-y-6">
      <div>
        <Link
          to="/admin/clienti"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Torna all'elenco
        </Link>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento cliente…
        </p>
      ) : null}

      {notFound ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Utente non trovato</AlertTitle>
          <AlertDescription>
            L'utente richiesto non esiste o non è un account business.
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError && !notFound ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(query.error)}</AlertDescription>
        </Alert>
      ) : null}

      {query.data ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            <WalletSummaryCard
              detail={query.data}
              lastTopUpAt={
                typeof query.data.account.lastTopUpAt === 'string'
                  ? query.data.account.lastTopUpAt
                  : null
              }
            />
            <Card>
              <CardHeader>
                <CardTitle>Modifica fido</CardTitle>
              </CardHeader>
              <CardContent>
                <FidoEditor detail={query.data} />
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Cronologia modifiche</CardTitle>
              </CardHeader>
              <CardContent>
                <AuditTrailViewer entries={query.data.fidoAuditEntries} />
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}
    </section>
  );
}
